import { apiClient } from './client';
import type { ReportStatus } from './reports';

/**
 * Data export (DATA-EXPORT): a participant exports their own data across every
 * procedure; a tenant_admin exports the whole tenant. The caller picks ONE
 * format — csv or json — and it applies to all the data. Request an async
 * build, then poll status until the zip is on S3 and a presigned URL comes
 * back. Reuses the report status shape (ready | processing | failed | none).
 */

export type ExportFormat = 'csv' | 'json';

/** Enqueue an export of the caller's own data in `format`. */
export async function requestMyExport(format: ExportFormat): Promise<void> {
  await apiClient.post('/api/v1/exports/me', null, { params: { format } });
}

/** Status + (when ready) a presigned download URL for the caller's export. */
export async function getMyExport(format: ExportFormat): Promise<ReportStatus> {
  const { data } = await apiClient.get<ReportStatus>('/api/v1/exports/me', {
    params: { format },
  });
  return data;
}

/** Enqueue an export of the whole tenant (tenant_admin) in `format`. */
export async function requestTenantExport(
  tenantId: string,
  format: ExportFormat,
): Promise<void> {
  await apiClient.post(`/api/v1/tenants/${tenantId}/export`, null, {
    params: { format },
  });
}

/** Status + (when ready) a presigned download URL for the tenant export. */
export async function getTenantExport(
  tenantId: string,
  format: ExportFormat,
): Promise<ReportStatus> {
  const { data } = await apiClient.get<ReportStatus>(
    `/api/v1/tenants/${tenantId}/export`,
    { params: { format } },
  );
  return data;
}
